import React, { useCallback, useState } from 'react';
import moment from 'moment';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCircle,
  faCircle1,
  faCircle2,
  faCircle3,
  faCircleCheck,
  faTriangleExclamation,
  faCircleExclamation,
  faPencil,
} from '@fortawesome/pro-light-svg-icons';
import { truncate } from 'lodash';
import styled from 'styled-components';
import {
  SideBar,
  SectionOptions,
  CheckGood,
  CheckError,
  Progress,
  CheckWarning,
} from '../styles';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import {
  FeedbackStatus,
  ReportStage,
} from 'donor/portal/pages/FeedbackDashboard/api';
import PoweredByLogo from './PoweredByLogo';
import EditReportNameModal from './EditReportNameModal';
import { updateReportName } from './Review/api';
import { useApolloClient } from '@apollo/react-hooks';
import { COLORS } from 'shared/design-system/settings/variables/Colors';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import DownloadReportQuestions from 'shared/components/DownloadReportQuestions';
import { useUserActions } from 'shared/auth/hooks/useUserActions';

const SidebarHeadingWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${SPACERS.small};
  gap: ${SPACERS.tiny};

  .heading-text {
    flex: 1;
    padding: 0;
  }

  .edit-icon {
    cursor: pointer;
    color: ${COLORS.grayDark};
    font-size: 14px;
    transition: color 0.2s ease;
    padding: ${SPACERS.extraTiny};

    &:hover {
      color: ${COLORS.ctaPrimary};
    }
  }
`;

function getByDisplayValue(
  reportStage: ReportStage | undefined,
  reportStatus: FeedbackStatus | undefined,
  sectionId: number,
): 'inherit' | 'none' {
  switch (reportStage) {
    case 'INITIAL':
      return sectionId === 0 ? 'inherit' : 'none';
    case 'INTERIM':
      return sectionId === 1 ? 'inherit' : 'none';
    case 'FINAL':
      if (reportStatus === 'Draft') {
        return sectionId === 0 ? 'inherit' : 'none';
      }
      return sectionId === 2 ? 'inherit' : 'none';

    default:
      return 'inherit';
  }
}

interface NavProps {
  handlePressOption: (sectionId: number, optionId: number) => unknown;
  handlePressSection: (sectionId: number) => unknown;
  handlePressStart: () => unknown;
  isThirdParty: boolean;
  reportStage?: ReportStage;
  reportStatus?: FeedbackStatus;
  isLoading: boolean | undefined;
  isStart: boolean;
  isReportPassed: boolean;
  title: string;
  sections: {
    focus: boolean;
    id: 1 | 2 | 3;
    sub: string;
    title: string;
    options: {
      error: boolean;
      focus: boolean;
      id: number;
      text: string;
      valid: boolean;
      warning: boolean;
    }[];
  }[];
  subTitle: string;
  tasks:
    | {
        submit?: string; // '2023-09-01'
        interim?: string; // '2023-12-01'
        finalize?: string; // '2024-01-01'
      }
    | undefined;
  reportId?: number | string;
  onReportNameUpdate?: (newName: string) => void;
}

export const Nav: React.FC<NavProps> = ({
  title,
  subTitle,
  isThirdParty,
  reportStatus,
  sections,
  isLoading,
  reportStage,
  handlePressSection,
  handlePressOption,
  tasks = {},
  isStart,
  handlePressStart,
  reportId,
  onReportNameUpdate,
}) => {
  const { isCNUser } = useUserActions();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [currentTitle, setCurrentTitle] = useState(title);
  const apolloClient = useApolloClient();
  const handleSaveReportName = async (newName: string) => {
    if (!reportId) return;

    try {
      await updateReportName(apolloClient, reportId, newName);
      setCurrentTitle(newName);
      if (onReportNameUpdate) {
        onReportNameUpdate(newName);
      }
    } catch (error) {
      console.error('Failed to update report name:', error);
      throw error;
    }
  };

  const getTask = useCallback(
    (secIdx: number) => {
      if (secIdx === 0) {
        return tasks?.submit
          ? `Forecast due ${moment(tasks.submit).format('DD MMM YYYY')}`
          : null;
      } else if (secIdx === 1) {
        return tasks?.interim
          ? `Due ${moment(tasks.interim).format('DD MMM YYYY')}`
          : null;
      } else {
        return tasks?.finalize
          ? `Due ${moment(tasks.finalize).format('DD MMM YYYY')}`
          : null;
      }
    },
    [tasks],
  );

  if (reportStatus === 'Finalized') return null;

  return (
    <SideBar className="report-nav-sidebar">
      <SidebarHeadingWrapper>
        <TrueImpactText variant="heading" className="heading-text">
          {currentTitle}
          {reportId && (
            <FontAwesomeIcon
              icon={faPencil}
              className="edit-icon"
              onClick={() => setIsEditModalOpen(true)}
              title="Edit report name"
            />
          )}
        </TrueImpactText>
      </SidebarHeadingWrapper>
      <EditReportNameModal
        open={isEditModalOpen}
        currentName={currentTitle}
        onClose={() => setIsEditModalOpen(false)}
        onSave={handleSaveReportName}
      />
      <div className={`section ${isStart ? 'selected' : ''}`}>
        <TrueImpactText
          variant="body-bold"
          onClick={reportStage === 'INITIAL' ? handlePressStart : undefined}
        >
          {subTitle}
        </TrueImpactText>
      </div>
      <Progress display={isLoading} />
      {!isLoading &&
        sections.map((sec, secIdx) => (
          <div
            key={sec.id}
            style={{
              display: getByDisplayValue(reportStage, reportStatus, secIdx),
            }}
            className={`section ${sec.focus ? 'active' : ''}`}
          >
            <div className="s-row">
              <TrueImpactText variant="body-bold" className="section-title">
                <FontAwesomeIcon
                  className="stage-icon"
                  icon={
                    sec.id === 1
                      ? faCircle1
                      : sec.id === 2
                      ? faCircle2
                      : faCircle3
                  }
                />
                {sec.title}
              </TrueImpactText>
              <FontAwesomeIcon icon={faCircle} />
            </div>
            {getTask(secIdx) && (
              <div className="s-row">
                <span className="section-subtitle">{getTask(secIdx)}</span>
              </div>
            )}
            <SectionOptions h={sec.focus ? sec.options.length * 44 : 0}>
              {sec.options.map((opt, optIdx) => {
                const isValid =
                  opt.text === 'Review & Submit' ? false : opt.valid;

                return (
                  <div key={opt.id} className="s-row">
                    <div
                      className={`s-label ${opt.focus ? 'active' : ''}`}
                      onClick={() => handlePressOption(secIdx, optIdx)}
                    >
                      <TrueImpactText className="s-name">
                        {truncate(opt.text, { length: 20 })}
                      </TrueImpactText>
                      {isValid ? (
                        <CheckGood>
                          <FontAwesomeIcon icon={faCircleCheck} />
                        </CheckGood>
                      ) : (
                        <FontAwesomeIcon icon={faCircle} />
                      )}
                      {opt.error && (
                        <CheckError>
                          <FontAwesomeIcon icon={faCircleExclamation} />
                        </CheckError>
                      )}
                      {!opt.error && opt.warning && (
                        <CheckWarning>
                          <FontAwesomeIcon icon={faTriangleExclamation} />
                        </CheckWarning>
                      )}
                    </div>
                  </div>
                );
              })}
            </SectionOptions>
          </div>
        ))}
      <DownloadReportQuestions
        isButton={true}
        text={
          isCNUser
            ? 'Preview questions & draft your report'
            : 'Download all report questions'
        }
        className="download-report-questions"
      />
      {isThirdParty && <PoweredByLogo className="powered-by-logo" />}
    </SideBar>
  );
};
